// Os dois nomes de cada boleto: quem assinou com a Vigor e quem é o titular da
// conta de luz na Equatorial.
//
// São papéis DIFERENTES de propósito (ver `src/lib/pagadorBoleto.ts`): existe
// assinante ativo cuja fatura de energia segue no nome de outra pessoa, porque
// a troca de titularidade na distribuidora não aconteceu. Medido em 15/09/2026:
// das 1.525 UCs não canceladas, 290 têm titular diferente do assinante e 146
// não têm titular no cadastro.
//
// `faturas.cliente_nome` NÃO serve para os dois: ele carrega o assinante (ou um
// nome antigo dele), nunca o titular — o titular só existe no cadastro da UC.
//
// Função pura, sem I/O nem JSX: roda sob `node --test` com type-stripping.

import { normalizeUcCodigo } from '../ucCodigo.ts';

export interface NomesDaUc {
  /** Quem assinou com a Vigor. Nunca vazio: cai no nome gravado na fatura. */
  assinante: string;
  /** Titular da conta de luz na Equatorial. `null` = não está no cadastro da UC. */
  titular: string | null;
}

/** O que o índice precisa do cadastro da UC. */
export interface UcNomes {
  numero_uc_norm?: string | null;
  outro_numero_uc_norm?: string | null;
  nome_cliente?: string | null;
  titular_fatura?: string | null;
}

/**
 * Índice por número de UC normalizado. A UC entra pelos DOIS números (o do dia a
 * dia e o código interno da distribuidora), porque `faturas.uc_codigo` ora vem
 * com um, ora com o outro — mesmo casamento de `buscarUcDaFatura`.
 */
export function indiceNomesUc(ucs: readonly UcNomes[]): Map<string, NomesDaUc> {
  const indice = new Map<string, NomesDaUc>();
  for (const uc of ucs) {
    const nomes: NomesDaUc = {
      assinante: (uc.nome_cliente || '').trim(),
      titular: (uc.titular_fatura || '').trim() || null,
    };
    for (const codigo of [uc.numero_uc_norm, uc.outro_numero_uc_norm]) {
      const chave = normalizeUcCodigo(codigo);
      // Primeiro cadastro vence: `deduplicarUcs` já documenta que a base tem a
      // mesma UC física cadastrada duas vezes, com os números trocados.
      if (chave && !indice.has(chave)) indice.set(chave, nomes);
    }
  }
  return indice;
}

/** Os dois nomes de uma fatura. Sem cadastro da UC, sobra o nome da própria fatura. */
export function nomesDaFatura(
  f: { uc_codigo?: string | null; cliente_nome?: string | null },
  indice: ReadonlyMap<string, NomesDaUc>,
): NomesDaUc {
  const chave = normalizeUcCodigo(f.uc_codigo);
  const doCadastro = chave ? indice.get(chave) : undefined;
  return {
    assinante: doCadastro?.assinante || (f.cliente_nome || '').trim() || 'Desconhecido',
    titular: doCadastro?.titular ?? null,
  };
}
